import { useEffect, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Image, Animated, ScrollView } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import { RADIUS, TRACKING } from '@/constants/designTokens';
import { DUEL_LOGO } from '../../assets/images';

export default function WelcomeScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const insets = useSafeAreaInsets();

  const logoOpacity = useRef(new Animated.Value(0)).current;
  const taglineTranslateY = useRef(new Animated.Value(30)).current;
  const taglineOpacity = useRef(new Animated.Value(0)).current;
  const btn1TranslateY = useRef(new Animated.Value(40)).current;
  const btn1Opacity = useRef(new Animated.Value(0)).current;
  const btn2TranslateY = useRef(new Animated.Value(40)).current;
  const btn2Opacity = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    Animated.sequence([
      Animated.timing(logoOpacity, { toValue: 1, duration: 600, useNativeDriver: true }),
      Animated.parallel([
        Animated.timing(taglineOpacity, { toValue: 1, duration: 400, useNativeDriver: true }),
        Animated.timing(taglineTranslateY, { toValue: 0, duration: 400, useNativeDriver: true }),
      ]),
      Animated.stagger(150, [
        Animated.parallel([
          Animated.timing(btn1Opacity, { toValue: 1, duration: 300, useNativeDriver: true }),
          Animated.timing(btn1TranslateY, { toValue: 0, duration: 300, useNativeDriver: true }),
        ]),
        Animated.parallel([
          Animated.timing(btn2Opacity, { toValue: 1, duration: 300, useNativeDriver: true }),
          Animated.timing(btn2TranslateY, { toValue: 0, duration: 300, useNativeDriver: true }),
        ]),
      ]),
    ]).start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ScrollView contentContainerStyle={{ flexGrow: 1 }} style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={[styles.content, { paddingTop: insets.top + 24 }]}>
        {/* Logo + Tagline grouped in center */}
        <View style={styles.heroGroup}>
          <Animated.View style={[styles.logoContainer, { opacity: logoOpacity }]}>
            <Image source={DUEL_LOGO} style={styles.logoImage} resizeMode="contain" accessibilityLabel="DUEL logo" />
          </Animated.View>

          <Animated.View
            style={[
              styles.taglineContainer,
              { opacity: taglineOpacity, transform: [{ translateY: taglineTranslateY }] },
            ]}>
            <View style={styles.pillarsRow}>
              <Text style={[styles.pillar, { color: theme.colors.accent }]}>STRENGTH</Text>
              <Text style={[styles.pillarDot, { color: theme.colors.textTertiary }]}>·</Text>
              <Text style={[styles.pillar, { color: theme.colors.accent }]}>COMPETITION</Text>
              <Text style={[styles.pillarDot, { color: theme.colors.textTertiary }]}>·</Text>
              <Text style={[styles.pillar, { color: theme.colors.accent }]}>CAMARADERIE</Text>
            </View>

            <Text style={[styles.subtitle, { color: theme.colors.textSecondary }]}>
              Prove it. Track it. Own it.
            </Text>
          </Animated.View>
        </View>

        {/* Buttons pinned to bottom */}
        <View style={styles.buttonContainer}>
          <Animated.View style={{ opacity: btn1Opacity, transform: [{ translateY: btn1TranslateY }] }}>
            <TouchableOpacity
              style={[styles.primaryButton, { backgroundColor: theme.colors.accent }]}
              onPress={() => router.push('/(auth)/signup')}
              accessibilityRole="button"
              accessibilityLabel="Get Started">
              <Text style={[styles.primaryButtonText, { color: theme.colors.accentForeground }]}>
                Get Started
              </Text>
            </TouchableOpacity>
          </Animated.View>

          <Animated.View style={{ opacity: btn2Opacity, transform: [{ translateY: btn2TranslateY }] }}>
            <TouchableOpacity
              style={[styles.secondaryButton, { borderColor: theme.colors.accent }]}
              onPress={() => router.push('/(auth)/signin')}
              accessibilityRole="button"
              accessibilityLabel="Sign In">
              <Text style={[styles.secondaryButtonText, { color: theme.colors.accent }]}>
                Sign In
              </Text>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    flex: 1,
    paddingHorizontal: 32,
    paddingBottom: 48,
  },
  heroGroup: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 32,
  },
  logoImage: {
    width: 220,
    height: 220,
  },
  taglineContainer: {
    alignItems: 'center',
    gap: 20,
    paddingHorizontal: 8,
    marginBottom: 16,
  },
  pillarsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  pillar: {
    fontSize: 16,
    fontFamily: TYPO.subheading.bold,
    letterSpacing: TRACKING.wider,
    textTransform: 'uppercase',
  },
  pillarDot: {
    fontSize: 18,
    fontWeight: '400',
  },
  subtitle: {
    fontSize: 14,
    fontFamily: TYPO.subheading.bold,
    textAlign: 'center',
    lineHeight: 22,
    marginTop: 12,
    letterSpacing: TRACKING.wide,
    textTransform: 'uppercase',
  },
  buttonContainer: {
    gap: 16,
    paddingTop: 24,
  },
  primaryButton: {
    paddingVertical: 18,
    borderRadius: RADIUS.none,
    alignItems: 'center',
  },
  primaryButtonText: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
  secondaryButton: {
    borderWidth: 2,
    paddingVertical: 18,
    borderRadius: RADIUS.none,
    alignItems: 'center',
  },
  secondaryButtonText: {
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
    textTransform: 'uppercase',
    letterSpacing: TRACKING.wide,
  },
});
